import { useEffect, useRef, useState } from "react";
import { Sparkle, PaperPlaneTilt, Trash, ArrowsClockwise, Warning, ClockCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* IA-1 — Copilot in-app : sélecteur OpenAI/Claude + modèle, historique, streaming SSE, analyse Pilotage, fallback confirmé. */
async function streamSse(url, body, { onMeta, onDelta, onDone, onError }) {
  const res = await fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) { let d = null; try { d = (await res.json()).detail; } catch {} onError({ code: d || `http_${res.status}` }); return; }
  const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
  for (;;) {
    const { value, done } = await reader.read(); if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
      const ev = /^event: (\w+)/m.exec(chunk)?.[1]; const data = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!ev || !data) continue;
      const payload = JSON.parse(data);
      if (ev === "meta") onMeta(payload); else if (ev === "delta") onDelta(payload.t); else if (ev === "done") onDone(payload); else if (ev === "error") onError(payload);
    }
  }
}

const ERRORS = {
  ai_quota_exceeded: "Budget IA atteint pour ce mois (ou limite journalière).", ai_real_data_blocked: "Mode Preview : l'IA n'est ouverte qu'aux comptes de test.",
  ai_byok_required: "Données réelles : clé fournisseur propre requise.", ai_legal_validation_required: "Conditions de traitement non validées.",
  ai_provider_unavailable: "Le fournisseur n'a pas répondu.", ai_context_too_large: "Contexte trop volumineux.", ai_fallback_not_allowed: "Bascule de moteur non autorisée par l'organisation.",
};

export default function AiCopilot({ feature = "copilot" }) {
  const [catalog, setCatalog] = useState(null);
  const [provider, setProvider] = useState("openai");
  const [tier, setTier] = useState("quality");
  const [convs, setConvs] = useState([]);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null); // {text, meta}
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const endRef = useRef(null);

  const loadConvs = async () => { try { setConvs((await api.get(`/ai/conversations`)).data); } catch {} };
  const loadUsage = async () => { try { setUsage((await api.get("/ai/usage/me")).data); } catch {} };
  useEffect(() => {
    api.get("/ai/catalog").then((r) => { setCatalog(r.data); setProvider(r.data.preferences.provider); setTier(r.data.preferences.tier); }).catch(() => setCatalog({ providers: [], enabled_for_user: false }));
    loadConvs(); loadUsage();
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, live]);

  const openConv = async (id) => {
    const r = await api.get(`/ai/conversations/${id}`); setConv(r.data); setMessages(r.data.messages); setError(null);
  };
  const changeModel = async (p, t) => {
    setProvider(p); setTier(t);
    try { await api.patch("/ai/preferences", { provider: p, tier: t }); } catch (e) { toast.error(e?.response?.data?.detail || "Préférence refusée"); }
  };
  const run = async (url, body, optimisticText) => {
    setBusy(true); setError(null); setLive({ text: "", meta: null });
    if (optimisticText) setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: optimisticText }]);
    let convId = conv?.id;
    await streamSse(url, body, {
      onMeta: (meta) => { convId = meta.conversation_id; setLive((l) => ({ ...l, meta })); },
      onDelta: (t) => setLive((l) => ({ ...l, text: (l?.text || "") + t })),
      onDone: () => {},
      onError: (e) => setError(ERRORS[e.code] || e.detail || e.code),
    });
    setBusy(false); setLive(null);
    if (convId) { await openConv(convId); }
    loadConvs(); loadUsage();
  };
  const send = async () => {
    const text = input.trim(); if (!text || busy) return; setInput("");
    let c = conv;
    if (!c) { c = (await api.post("/ai/conversations", { feature })).data; setConv(c); setMessages([]); }
    await run(`${API}/ai/conversations/${c.id}/messages`, { text, provider, tier }, text);
  };
  const analyse = async () => { setConv(null); setMessages([]); await run(`${API}/ai/pilotage/analyse`, { text: "Analyse mon pilotage commercial et donne-moi une préconisation pour la semaine.", provider, tier }, null); };
  const retryOther = async () => {
    if (!conv) return;
    const other = provider === "openai" ? "anthropic" : "openai";
    if (!window.confirm(`Renvoyer la même question à ${other === "openai" ? "OpenAI" : "Claude"} ? Vos données (pseudonymisées) seront transmises à ce fournisseur.`)) return;
    await run(`${API}/ai/conversations/${conv.id}/retry`, { provider: other, tier, confirm: true }, null);
  };
  const remove = async (id) => { await api.delete(`/ai/conversations/${id}`); if (conv?.id === id) { setConv(null); setMessages([]); } loadConvs(); };

  if (!catalog) return null;
  const prov = catalog.providers.find((p) => p.id === provider);
  const modelLabel = prov?.tiers.find((t) => t.id === tier)?.label;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mt-4" data-testid="ai-copilot">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><Sparkle size={14} weight="fill" className="text-amber-500" /> Assistant IA</div>
        <select value={provider} onChange={(e) => changeModel(e.target.value, tier)} className="h-8 rounded-lg border border-slate-200 text-xs px-2" data-testid="ai-provider-select">
          {catalog.providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select value={tier} onChange={(e) => changeModel(provider, e.target.value)} className="h-8 rounded-lg border border-slate-200 text-xs px-2" data-testid="ai-model-select">
          {(prov?.tiers || []).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        {usage && <span className="ml-auto text-[11px] text-slate-500" data-testid="ai-usage-badge">{usage.spent_usd.toFixed(3)} $ / {usage.budget_usd.toFixed(0)} $ ce mois{usage.alert_80 ? " · ⚠ 80 %" : ""}</span>}
      </div>
      {catalog.enabled_for_user === false && <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3" data-testid="ai-disabled-notice">Mode Preview : l'IA est réservée aux comptes de test (données fictives) tant que les conditions de traitement ne sont pas validées.</div>}
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
        <aside className="rounded-xl bg-slate-50 p-2 max-h-80 overflow-auto" data-testid="ai-conversation-list">
          <button type="button" onClick={() => { setConv(null); setMessages([]); setError(null); }} className="w-full text-left text-xs font-semibold px-2 py-1.5 rounded-lg hover:bg-white" data-testid="ai-new-conversation">+ Nouvelle conversation</button>
          {convs.map((c) => (
            <div key={c.id} className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs cursor-pointer ${conv?.id === c.id ? "bg-white font-semibold" : "hover:bg-white/60"}`} onClick={() => openConv(c.id)} data-testid="ai-conversation-item">
              <ClockCounterClockwise size={12} className="text-slate-400 shrink-0" /><span className="truncate flex-1">{c.title}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500" data-testid="ai-conversation-delete"><Trash size={12} /></button>
            </div>
          ))}
        </aside>
        <section className="flex flex-col min-h-[240px]">
          <div className="flex-1 space-y-2 max-h-80 overflow-auto pr-1" data-testid="ai-messages">
            {messages.length === 0 && !live && <p className="text-xs text-slate-400 p-2">Posez une question ou lancez une analyse de votre pilotage. Les données transmises sont pseudonymisées ; l'IA ne modifie jamais vos dossiers.</p>}
            {messages.map((m) => (
              <div key={m.id} className={`rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-slate-900 text-white ml-8" : "bg-slate-50 text-slate-800 mr-4"}`} data-testid={`ai-message-${m.role}`}>
                {m.content || (m.status === "error" ? "— erreur fournisseur —" : "")}
                {m.role === "assistant" && m.model && <div className="text-[10px] text-slate-400 mt-1" data-testid="ai-message-model">{m.provider === "openai" ? "OpenAI" : "Claude"} · {m.model} · {m.tokens_in}+{m.tokens_out} tokens · ≈{Number(m.cost_estimate_usd || 0).toFixed(4)} $</div>}
              </div>
            ))}
            {live && <div className="rounded-xl px-3 py-2 text-sm whitespace-pre-wrap bg-slate-50 text-slate-800 mr-4" data-testid="ai-stream">{live.text || "…"}<div className="text-[10px] text-slate-400 mt-1">{live.meta ? `${live.meta.model_label} · en cours` : "connexion…"}</div></div>}
            <div ref={endRef} />
          </div>
          {error && <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-2" data-testid="ai-error"><Warning size={14} /> {error}
            {conv && catalog.fallback_allowed && <button type="button" onClick={retryOther} className="ml-auto underline" data-testid="ai-retry-other">Réessayer avec l'autre moteur</button>}</div>}
          <div className="flex items-center gap-2 mt-2">
            {feature === "pilotage" && <button type="button" onClick={analyse} disabled={busy} className="h-9 px-3 rounded-lg bg-amber-500 text-white text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="ai-analyse-button"><Sparkle size={14} weight="fill" /> Analyser avec l'IA</button>}
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={`Question à ${modelLabel || "l'IA"}…`} className="flex-1 h-9 rounded-lg border border-slate-200 px-3 text-sm" data-testid="ai-input" />
            <button type="button" onClick={send} disabled={busy || !input.trim()} className="h-9 w-9 rounded-lg bg-slate-900 text-white inline-flex items-center justify-center disabled:opacity-40" data-testid="ai-send">{busy ? <ArrowsClockwise size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}</button>
          </div>
        </section>
      </div>
    </div>
  );
}
